// @flow
import React, { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';

import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../../utils/stylesUtils';
import { setSnackMessage } from '../../../reduxStore/modules/common/actions';
import { downloadCsvTemplate, formatFileSize } from '../csvTemplateUtils';
import fileSelectViewStyles from './FileSelectView.Style';

type Props = {
  onFileSelected: (file: File) => void,
  onCancel: () => void
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const FileSelectView = ({ onFileSelected, onCancel }: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = createClasses(fileSelectViewStyles, theme);

  const onDrop = useCallback((acceptedFiles: Array<File>, rejectedFiles: Array<Object>) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      const errorCode = rejection.errors?.[0]?.code;

      let message = 'File was rejected.';
      if (errorCode === 'file-too-large') {
        message = 'File exceeds maximum size (5MB).';
      } else if (errorCode === 'file-invalid-type') {
        message = 'Only CSV files are supported. Please upload a .csv file.';
      }

      dispatch(setSnackMessage({
        message,
        level: 'error',
        duration: 5000
      }));
      return;
    }

    // Handle accepted file
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      // Double-check file size
      if (file.size > MAX_FILE_SIZE) {
        dispatch(setSnackMessage({
          message: 'File exceeds maximum size (5MB).',
          level: 'error',
          duration: 5000
        }));
        return;
      }

      onFileSelected(file);
    }
  }, [dispatch, onFileSelected]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    acceptedFiles
  } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv']
    },
    multiple: false,
    maxSize: MAX_FILE_SIZE
  });

  const selectedFile = acceptedFiles[0];

  const dropzoneStyle = useMemo(() => ({
    ...classes.dropzone,
    ...(isDragActive ? classes.dropzoneActive : {}),
    ...(isDragAccept ? classes.dropzoneAccept : {}),
    ...(isDragReject ? classes.dropzoneReject : {})
  }), [classes, isDragActive, isDragAccept, isDragReject]);

  const handleDownloadTemplate = (e: Object) => {
    e.preventDefault();
    e.stopPropagation();
    downloadCsvTemplate();
  };

  return (
    <Box sx={classes.container}>
      <Box {...getRootProps()} sx={dropzoneStyle}>
        <input {...getInputProps()} />

        <Box sx={classes.dropzoneContent}>
          <CloudUploadIcon sx={classes.uploadIcon} />

          <Typography variant="h6" sx={classes.title}>
            {isDragActive
              ? 'Drop your CSV file here...'
              : 'Drag and drop your CSV file here'}
          </Typography>

          <Typography variant="body2" sx={classes.subtitle}>
            or click to browse files
          </Typography>

          {selectedFile && (
            <Box sx={classes.selectedFile}>
              <InsertDriveFileIcon sx={classes.fileIcon} />
              <Box>
                <Typography variant="body2" sx={classes.fileName}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" sx={classes.fileSize}>
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={classes.instructions}>
        <Typography variant="subtitle2" sx={classes.instructionTitle}>
          CSV File Requirements:
        </Typography>
        <Typography variant="body2" component="div" sx={classes.instructionList}>
          <ul>
            <li><strong>Required columns:</strong> First Name, Last Name, Phone</li>
            <li><strong>Optional columns:</strong> Email, Tags</li>
            <li><strong>Tags format:</strong> Use comma-separated values for multiple tags<br/>
              <span style={{ color: theme.palette.text.secondary }}>
                Example: &quot;VIP,Preferred Customer&quot;
              </span>
            </li>
            <li>New tags will be created automatically if they don&apos;t exist</li>
            <li>Maximum file size: 5MB</li>
          </ul>
        </Typography>

        <Link
          component="button"
          variant="body2"
          onClick={handleDownloadTemplate}
          sx={classes.templateLink}
        >
          <DownloadIcon sx={classes.downloadIcon} />
          Download CSV Template
        </Link>
      </Box>

      <Box sx={classes.actions}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={classes.cancelButton}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedFile}
          onClick={() => selectedFile && onFileSelected(selectedFile)}
          sx={classes.uploadButton}
        >
          Upload
        </Button>
      </Box>
    </Box>
  );
};

export default FileSelectView;
